import { MaxLengthValidatorDirective } from '../../shared/directives/max-length-validator.directive';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { PageTitleService } from '../../core/services/page-title.service';
import { MemberApiService } from '../../core/services/member-api.service';
import { environment } from '../../../environments/environment';
import { AuthStateService } from '../../core/services/auth-state.service';
import { MemberProfileStore } from './application/member-profile.store';
import { MemberProfile, ProfileCommand, ProfileEditor, RELATIONSHIPS, SUCCESS_MESSAGES } from './domain/member-profile';
import { ProfileEditorDialogComponent } from './ui/profile-editor-dialog.component';

@Component({
    selector: 'app-member',
    imports: [MaxLengthValidatorDirective, FormsModule, ProfileEditorDialogComponent],
    templateUrl: './member.component.html'
})
export class MemberComponent implements OnInit, OnDestroy {
  private pageTitle = inject(PageTitleService);
  private memberApi = inject(MemberApiService);
  private toastr = inject(ToastrService);
  private auth = inject(AuthStateService);
  private router = inject(Router);
  readonly profile = inject(MemberProfileStore);
  editor: ProfileEditor | null = null;
  editorTrigger: HTMLElement | null = null;
  outcomeChecked = false;
  private alive = true;
  get member(): Readonly<Partial<MemberProfile>> { return this.profile.profile ?? {}; }
  get paymentMethod() { return { name: ['口座振替', 'クレジットカード'][this.member.payment?.paymentMethod ?? -1] ?? '' }; }
  get relationship(): string { return RELATIONSHIPS[this.member.emRelationship ?? -1] ?? ''; }

  shopId = environment.shopId;
  changeToCreditCard = 1;
  singlePayment: any = {};
  getGmo: any = {};

  async ngOnInit(): Promise<void> {
    this.pageTitle.set('fa s-icon-member', '会員情報');
    await this.profile.enter();
  }

  async loadMember(): Promise<void> {
    await this.profile.refresh();
  }

  openEditor(kind: ProfileEditor, event?: Event): void {
    if (!this.profile.profile || this.profile.blocked || this.profile.loading || this.profile.loadError) return;
    this.profile.beginEdit(); this.outcomeChecked = false;
    this.editorTrigger = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.editor = kind;
  }
  closeEditor(): void { if (!this.profile.busy) this.editor = null; }
  async saveProfile(command: ProfileCommand): Promise<void> {
    const confirmed = await this.profile.submit(command);
    if (confirmed && this.alive) {
      this.editor = null;
      this.toastr.success(SUCCESS_MESSAGES[command.kind], '', { timeOut: 5000 });
    }
  }
  acknowledgeOutcome(): void {
    if (this.outcomeChecked) { this.profile.acknowledgeOutcome(); this.outcomeChecked = false; }
  }
  loginAgain(): void {
    if (this.profile.busy) return;
    this.auth.logout(); this.auth.returnToPath = '/member';
    void this.router.navigate(['/login']);
  }
  ngOnDestroy(): void { this.alive = false; }

  async getPayment(memberId: number | string): Promise<void> {
    this.singlePayment = {};
    this.getGmo = {};
    const data = await this.memberApi.getPayment(memberId);
    this.singlePayment = data?.payment ?? {};
    this.singlePayment.memberCode = data?.memberCode;
    this.singlePayment.paymentMethod = String(this.changeToCreditCard);
    if (data?.gmo) {
      this.getGmo = { ...data.gmo };
      this.getGmo.expireYearAt = String(data.gmo.Expire ?? '').substring(0, 2);
      this.getGmo.expireMonthAt = String(data.gmo.Expire ?? '').substring(2);
      this.getGmo.security = '****';
    }
  }

  async savePayment(): Promise<void> {
    delete this.singlePayment.status;
    delete this.singlePayment.id;
    this.singlePayment.paymentMethod = String(this.changeToCreditCard);
    const memberId = this.singlePayment.memberId;
    if (parseInt(this.singlePayment.paymentMethod) === 1) {
      const gmo = this.getGmo;
      const expire = String(gmo.expireYearAt) + ('0' + String(gmo.expireMonthAt)).slice(-2);
      (window as any).Multipayment.init(this.shopId);
      (window as any).Multipayment.getToken({
        holdername: String(gmo.HolderName),
        cardno: String(gmo.CardNo),
        expire,
        securitycode: String(gmo.security),
      }, async (res: any) => {
        if (res.resultCode === '000' && res.tokenObject?.token) {
          this.singlePayment.gmo = { username: gmo.HolderName, token: res.tokenObject.token };
          const data = await this.memberApi.createPayment(memberId, this.singlePayment);
          if (!data?.message) {
            this.toastr.success('支払方法を更新しました', '', { timeOut: 5000 });
            await this.loadMember();
          } else {
            this.toastr.warning(data.message, '', { timeOut: 5000 });
          }
        } else {
          this.toastr.warning('クレジットカード情報を再度入力してください', '', { timeOut: 5000 });
        }
      });
    } else {
      if (this.singlePayment.btAccountName) {
        this.singlePayment.btAccountName = convertFullToHalf(this.singlePayment.btAccountName);
      }
      await this.memberApi.createPayment(memberId, this.singlePayment);
      this.toastr.success('支払方法を更新しました', '', { timeOut: 5000 });
      await this.loadMember();
    }
  }
}

function convertFullToHalf(before: string): string {
  const full = ['ア','イ','ウ','エ','オ','カ','キ','ク','ケ','コ','サ','シ','ス','セ','ソ','タ','チ','ツ','テ','ト','ナ','ニ','ヌ','ネ','ノ','ハ','ヒ','フ','ヘ','ホ','マ','ミ','ム','メ','モ','ヤ','ヰ','ユ','ヱ','ヨ','ラ','リ','ル','レ','ロ','ワ','ヲ','ン','ガ','ギ','グ','ゲ','ゴ','ザ','ジ','ズ','ゼ','ゾ','ダ','ヂ','ヅ','デ','ド','バ','ビ','ブ','ベ','ボ','パ','ピ','プ','ペ','ポ','ァ','ィ','ゥ','ェ','ォ','ャ','ュ','ョ','ッ','゛','°','、','。','「','」','ー','・'];
  const half = ['ｱ','ｲ','ｳ','ｴ','ｵ','ｶ','ｷ','ｸ','ｹ','ｺ','ｻ','ｼ','ｽ','ｾ','ｿ','ﾀ','ﾁ','ﾂ','ﾃ','ﾄ','ﾅ','ﾆ','ﾇ','ﾈ','ﾉ','ﾊ','ﾋ','ﾌ','ﾍ','ﾎ','ﾏ','ﾐ','ﾑ','ﾒ','ﾓ','ﾔ','ｲ','ﾕ','ｴ','ﾖ','ﾗ','ﾘ','ﾙ','ﾚ','ﾛ','ﾜ','ｦ','ﾝ','ｶﾞ','ｷﾞ','ｸﾞ','ｹﾞ','ｺﾞ','ｻﾞ','ｼﾞ','ｽﾞ','ｾﾞ','ｿﾞ','ﾀﾞ','ﾁﾞ','ﾂﾞ','ﾃﾞ','ﾄﾞ','ﾊﾞ','ﾋﾞ','ﾌﾞ','ﾍﾞ','ﾎﾞ','ﾊﾟ','ﾋﾟ','ﾌﾟ','ﾍﾟ','ﾎﾟ','ｧ','ｨ','ｩ','ｪ','ｫ','ｬ','ｭ','ｮ','ｯ','ﾞ','ﾟ','､','｡','｢','｣','ｰ','･'];
  let after = '';
  for (let i = 0; i < before.length; i++) {
    const idx = full.indexOf(before[i]);
    after += idx >= 0 ? half[idx] : before[i];
  }
  return after.replace(/　/g, ' ');
}
